import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useHideLayoutFooter } from '@/contexts/LayoutChromeContext';
import { getValidAccessToken } from '@/services/supabase';
import { getHowieApiUrl } from '@/config/howieService';
import type { DojoRoundReview } from '@/types/dojo';
import { VignettePanel } from '@/components/unit/VignettePanel';
import { UnitChat } from '@/components/unit/UnitChat';
import { getImperativeIcon } from '@/components/unit/imperatives';
import { cn } from '@/lib/utils';

const STATUS_LABEL: Record<DojoRoundReview['status'], string> = {
  completed: 'Completed',
  in_progress: 'In progress',
  abandoned: 'Ended early',
};

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

/**
 * DojoRoundReviewPage — a completed unit, reopened.
 *
 * Read-only by construction: no onMarksChange is passed to VignettePanel, so
 * marking is off and the learner's own past marks render for reference only.
 * The server-side payload this fetches has already stripped the answer key —
 * see get_dojo_round_review — so there is nothing here to accidentally reveal.
 */
export default function DojoRoundReviewPage() {
  const { roundId } = useParams<{ roundId: string }>();
  const [review, setReview] = useState<DojoRoundReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useHideLayoutFooter(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!roundId) {
        setError('No round to show.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const accessToken = await getValidAccessToken();
        if (!accessToken) {
          if (!cancelled) {
            setError('Sign in again to view this round.');
            setLoading(false);
          }
          return;
        }
        const response = await fetch(`${getHowieApiUrl()}/api/dojo/rounds/${roundId}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.status === 404) {
          throw new Error("That round doesn't exist, or isn't yours.");
        }
        if (!response.ok) {
          throw new Error(`Round request failed (${response.status})`);
        }
        const payload = (await response.json()) as DojoRoundReview & { error?: string };
        if (payload.error) throw new Error(payload.error);
        if (!cancelled) setReview(payload);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load this round.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [roundId]);

  if (loading) {
    return (
      <div className="flex h-full min-h-[60vh] items-center justify-center">
        <Loader2 className="animate-spin text-how-space-deep/40" size={22} aria-hidden="true" />
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <p className="text-sm text-how-text-on-paper/70">{error ?? 'Could not load this round.'}</p>
        <Link
          to="/dojo"
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-how-accent-warm hover:underline"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Back to Dojo
        </Link>
      </div>
    );
  }

  const { unit } = review;
  const Icon = getImperativeIcon(unit.imperative_id);
  const date = formatDate(review.completed_at ?? review.started_at);
  const hasIntakeAnswers = Boolean(
    review.intake_answers.challenging || review.intake_answers.natural
  );

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <div className="flex-shrink-0 border-b border-gray-100 px-4 py-3 sm:px-8">
        <Link
          to="/dojo"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-how-text-on-paper/60 hover:text-how-space-deep"
        >
          <ArrowLeft size={13} aria-hidden="true" />
          Back to Dojo
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-how-accent-warm">
            <Icon size={14} aria-hidden="true" />
            {unit.imperative_label}
          </span>
          <span
            className={cn(
              'rounded-full px-2.5 py-0.5 text-[11px] font-medium',
              review.status === 'completed'
                ? 'bg-how-accent-warm/10 text-how-accent-warm'
                : 'bg-how-space-deep/[0.06] text-how-text-on-paper/60'
            )}
          >
            {STATUS_LABEL[review.status]}
          </span>
          {date && <span className="text-[11px] text-how-text-on-paper/40">{date}</span>}
        </div>
      </div>

      {/* A finished round is read start to finish, not operated. The live round's
          two-column surface exists so the text and the conversation can be worked
          at the same time; in review that split just means scrolling two panes to
          reconstruct one sequence. This is the sequence: what they brought, the
          story as they marked it, the conversation, the flipped case and their
          move, and the sentence they left with. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl space-y-10 px-4 py-8 sm:px-6">
          {hasIntakeAnswers && (
            <section className="space-y-3">
              <h2 className="text-[11px] font-medium uppercase leading-4 tracking-wide text-how-text-on-paper/50">
                What you brought
              </h2>
              {review.intake_answers.challenging && (
                <div className="space-y-1">
                  <p className="text-xs text-how-text-on-paper/60">
                    {unit.intake_questions[0]}
                  </p>
                  <p className="text-sm leading-6 text-how-space-deep">
                    {review.intake_answers.challenging}
                  </p>
                </div>
              )}
              {review.intake_answers.natural && (
                <div className="space-y-1">
                  <p className="text-xs text-how-text-on-paper/60">
                    {unit.intake_questions[1]}
                  </p>
                  <p className="text-sm leading-6 text-how-space-deep">
                    {review.intake_answers.natural}
                  </p>
                </div>
              )}
            </section>
          )}

          <section className="space-y-3">
            <h2 className="text-[11px] font-medium uppercase leading-4 tracking-wide text-how-text-on-paper/50">
              {review.marks.length > 0 ? 'The story, as you marked it' : 'The story'}
            </h2>
            {/* beat="highlight" so this opens on the exemplar the marks belong to.
                "completed" would have opened on the flipped case and left the
                learner's own marks behind a tab. No onMarksChange: read-only. */}
            <VignettePanel unit={unit} beat="highlight" marks={review.marks} />
          </section>

          <section className="space-y-3">
            <h2 className="text-[11px] font-medium uppercase leading-4 tracking-wide text-how-text-on-paper/50">
              The conversation
            </h2>
            <UnitChat
              messages={review.messages}
              streamingText=""
              isStreaming={false}
              isSending={false}
            />
          </section>

          {review.insight && (
            <section className="space-y-2 rounded-xl border-l-2 border-how-accent-warm/40 bg-white/60 p-4">
              <h2 className="text-[11px] font-medium uppercase leading-4 tracking-wide text-how-text-on-paper/50">
                What you took with you
              </h2>
              <p className="text-sm leading-6 text-how-space-deep">{review.insight}</p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
